import { useState, useEffect } from "react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import {
  readCookieConsent,
  setCookieConsent,
  type CookieConsent,
} from "@/shared/lib/analytics";

export function CookieBanner() {
  const { locale } = useLanguage();
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Homepage demo iframes run the whole app; the parent page owns consent.
    if (window.self !== window.top) return;
    const stored = readCookieConsent();
    if (stored) {
      setConsent(stored);
    } else {
      // Small delay so banner doesn't flash on initial render
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handle = (value: CookieConsent) => {
    setCookieConsent(value);
    setConsent(value);
    setVisible(false);
  };

  if (consent !== null || !visible) return null;

  const text = (
    {
      en: {
        message:
          "We use cookies to keep you signed in and to understand how the platform is used.",
        privacy: "Privacy Policy",
        accept: "Accept all",
        decline: "Essential only",
      },
      uk: {
        message:
          "Ми використовуємо файли cookie для авторизації та аналізу використання платформи.",
        privacy: "Політика конфіденційності",
        accept: "Прийняти всі",
        decline: "Тільки необхідні",
      },
      pl: {
        message:
          "Używamy plików cookie, aby utrzymać sesję i analizować korzystanie z platformy.",
        privacy: "Polityka prywatności",
        accept: "Akceptuję wszystkie",
        decline: "Tylko niezbędne",
      },
      ro: {
        message:
          "Folosim cookie-uri pentru a menține sesiunea și a înțelege cum este utilizată platforma.",
        privacy: "Politica de confidențialitate",
        accept: "Accept toate",
        decline: "Doar esențiale",
      },
    } as Record<
      string,
      { message: string; privacy: string; accept: string; decline: string }
    >
  )[locale] ?? {
    message:
      "We use cookies to keep you signed in and to understand how the platform is used.",
    privacy: "Privacy Policy",
    accept: "Accept all",
    decline: "Essential only",
  };

  return (
    <div
      className="fixed bottom-14 sm:bottom-16 left-0 right-0 z-50 px-4 pt-4 sm:px-6 sm:pt-6 pointer-events-none"
      style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
    >
      <div className="max-w-2xl mx-auto bg-white border border-neutral-200 rounded-md shadow-2xl shadow-neutral-900/10 p-5 sm:p-6 pointer-events-auto animate-fade-up">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-neutral-600 leading-relaxed">
              {text.message}{" "}
              <button
                onClick={() => (window as any).navigate?.("/privacy")}
                className="text-primary-600 hover:underline font-medium cursor-pointer"
              >
                {text.privacy}
              </button>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => handle("declined")}
              className="px-4 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {text.decline}
            </button>
            <button
              onClick={() => handle("accepted")}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {text.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
